import type { RevenueAnalyticsRow } from './types';
import { faSortAlt } from '@fortawesome/pro-regular-svg-icons/faSortAlt';
import { faSortDown } from '@fortawesome/pro-regular-svg-icons/faSortDown';
import { faSortUp } from '@fortawesome/pro-regular-svg-icons/faSortUp';
import type { RevenueAnalyticsSort, RevenueAnalyticsSortColumn } from 'src/admin/models/RevenueAnalyticsModel/state';

/** Four-decimal price formatting shared by both revenue tables. */
export const priceFmt = (value: number): string => value.toFixed(4);

/** Header sort glyph for a column under the given active sort. */
export function sortIcon(sort: RevenueAnalyticsSort | null, column: RevenueAnalyticsSortColumn) {
    if (sort?.column !== column) return faSortAlt;
    return sort.direction === 'asc' ? faSortUp : faSortDown;
}

/** `aria-sort` value for a column header under the given active sort. */
export function ariaSort(
    sort: RevenueAnalyticsSort | null,
    column: RevenueAnalyticsSortColumn,
): 'ascending' | 'descending' | 'none' {
    if (sort?.column !== column) return 'none';
    return sort.direction === 'asc' ? 'ascending' : 'descending';
}

/**
 * Fraction (0–1) of start-of-day inventory still remaining. Returns `null`
 * when the backend reports no inventory for the row (so the UI can render a
 * placeholder rather than a misleading 0), and 0 when SOD is present but zero.
 */
export function percentRemaining(row: Pick<RevenueAnalyticsRow, 'sodInventory' | 'remainingInventory'>): number | null {
    if (row.sodInventory === null) return null;
    if (row.sodInventory <= 0) return 0;
    // Clamp to [0, 1]: a row can occasionally report more remaining than SOD
    // after intraday adjustments, which would otherwise overflow the bar.
    return Math.min(1, Math.max(0, (row.remainingInventory ?? 0) / row.sodInventory));
}

/**
 * Fraction (0–1) of start-of-day inventory consumed. The complement of
 * {@link percentRemaining}, so the two always sum to 1 for a row with
 * inventory; `null` when the row has no inventory data.
 */
export function percentUsed(row: Pick<RevenueAnalyticsRow, 'sodInventory' | 'remainingInventory'>): number | null {
    if (row.sodInventory === null) return null;
    if (row.sodInventory <= 0) return 0;
    return 1 - (percentRemaining(row) ?? 0);
}
